import React, { useState } from 'react';
import { Phone, MessageCircle, Mail, MapPin, Clock, Send, Instagram, Facebook, Youtube } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <SEOHead
        title="Contact RRIDE Moto Studio - Get in Touch"
        description="Contact RRIDE Moto Studio for premium motorcycle accessories, bike gear, and customer support. Reach us via phone, email, WhatsApp or visit our store."
        canonical="/contact"
        pathname="/contact"
      />
      <main className="min-h-screen bg-gradient-to-br from-[#102318] via-[#183326] to-[#0b1710] text-white pt-20 px-4 pb-16 font-[Cormorant_Garamond]">
      <section className="max-w-6xl mx-auto">
        <p className="text-[#ffcc32] font-black uppercase text-sm tracking-widest">Contact Us</p>
        <h1 className="text-5xl md:text-7xl uppercase mt-2 mb-8" style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}>
          Get In Touch
        </h1>
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold uppercase mb-4 text-[#ffcc32]">RRide Moto Studio</h2>
              <div className="bg-black/30 border border-white/10 rounded-lg p-6">
                <div className="flex items-start gap-3 mb-4">
                  <MapPin className="text-[#ffcc32] mt-1" size={20} />
                  <div>
                    <p className="font-semibold mb-1">Business Address</p>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      Ground Floor, Mahaveer Building,<br />
                      Mandai - Dhamankar Naka Road,<br />
                      Near Payal Talkies,<br />
                      Narpoli, Bhiwandi,<br />
                      Maharashtra 421305, India
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <a 
                href="tel:+918793678780"
                className="bg-black/30 border border-white/10 rounded-lg p-6 hover:border-orange-500/50 transition"
              >
                <Phone size={32} className="text-orange-500 mb-4" />
                <h3 className="text-lg font-black uppercase mb-2">Phone</h3>
                <p className="text-zinc-400">+91 8793678780</p>
              </a>

              <a 
                href="https://wa.me/918793678780"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black/30 border border-white/10 rounded-lg p-6 hover:border-green-500/50 transition"
              >
                <MessageCircle size={32} className="text-green-500 mb-4" />
                <h3 className="text-lg font-black uppercase mb-2">WhatsApp</h3>
                <p className="text-zinc-400">+91 8793678780</p>
              </a>
            </div>

            <div className="space-y-4">
              <a 
                href="mailto:rridemods@gmail.com"
                className="bg-black/30 border border-white/10 rounded-lg p-6 hover:border-blue-500/50 transition block"
              >
                <Mail size={32} className="text-blue-500 mb-4" />
                <h3 className="text-lg font-black uppercase mb-2">Support Email</h3>
                <p className="text-zinc-400">rridemods@gmail.com</p>
              </a>

              <a 
                href="mailto:outransystems@gmail.com"
                className="bg-black/30 border border-white/10 rounded-lg p-6 hover:border-purple-500/50 transition block"
              >
                <Mail size={32} className="text-purple-500 mb-4" />
                <h3 className="text-lg font-black uppercase mb-2">Technical Support</h3>
                <p className="text-zinc-400">outransystems@gmail.com</p>
              </a>
            </div>

            <div className="bg-black/30 border border-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="text-[#ffcc32]" size={24} />
                <h3 className="text-lg font-black uppercase">Business Hours</h3>
              </div>
              <div className="space-y-2 text-zinc-400">
                <p><span className="text-white font-semibold">Monday – Saturday:</span> 10:00 AM – 8:00 PM</p>
                <p><span className="text-white font-semibold">Sunday:</span> Closed</p>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-black/30 border border-white/10 rounded-lg p-6">
              <h3 className="text-lg font-black uppercase mb-4">Follow Us</h3>
              <div className="flex gap-4">
                <a href="#" className="bg-gradient-to-br from-purple-600 to-pink-600 p-3 rounded-lg hover:opacity-80 transition">
                  <Instagram size={24} />
                </a>
                <a href="#" className="bg-blue-600 p-3 rounded-lg hover:opacity-80 transition">
                  <Facebook size={24} />
                </a>
                <a href="#" className="bg-red-600 p-3 rounded-lg hover:opacity-80 transition">
                  <Youtube size={24} />
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-black/30 border border-white/10 rounded-lg p-8">
            <h2 className="text-2xl font-bold uppercase mb-6 text-[#ffcc32]">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 uppercase">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffcc32] focus:outline-none transition"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 uppercase">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffcc32] focus:outline-none transition"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 uppercase">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffcc32] focus:outline-none transition"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 uppercase">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffcc32] focus:outline-none transition"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 uppercase">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffcc32] focus:outline-none transition resize-none"
                  placeholder="Tell us more about your query..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#ffcc32] text-black font-black uppercase py-4 rounded-lg hover:bg-[#ffda5c] transition flex items-center justify-center gap-2"
              >
                <Send size={20} />
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>
    </>
  );
}
